// React
import React from "react";

// External libraries
import { faTimes } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { Button } from "../buttons/button";
import { HorizontalDragPanel } from "../drag-handle/drag-panel";
import { getDataTestId } from "../functions/data-test-ids";
import { Table } from "../tables/table";
import { Panel, PanelExplanation } from "./panel";

function TablePanel(props) {
    const collapseHandler = (value) => {
        if (typeof props.OnCollapse === "function") {
            props.OnCollapse(value);
        }
    };

    const reorderHandler = (id, startIndex, endIndex) => {
        if (typeof props.Reorder === "function") {
            props.Reorder(id, startIndex, endIndex);
        }
    };

    const changeValue = (id, fieldName, value) => {
        if (typeof props.ChangeValue === "function") {
            props.ChangeValue(id, fieldName, value);
        }
    };

    const updateValue = (id, fieldName, value) => {
        if (typeof props.UpdateValue === "function") {
            props.UpdateValue(id, fieldName, value);
        }
    };

    const getPanelBodyClass = () => {
        let className = "eone-ui-panel-table-content"
        if(props.Card) {
            className += " eone-ui-panel-table-content-card"
        }
        return className
    }

    const hasRows = props.Rows && props.Rows.length > 0;
    const detailsPanelOpen = props.AllowDetailsPanel
        && props.TableState?.DetailsPanelOpen === true
        && !props.Loading
        && hasRows;
    const sidePanelOpen = Boolean(props.SidePanel) && props.SidePanelOpen === true;

    const getDetailsPanel = () => {
        if (!detailsPanelOpen) return null;

        const selectedRow = props.SelectedRowId != null && props.Rows
            ? props.Rows.find(row => row[props.IdField] === props.SelectedRowId)
            : null;

        const content = typeof props.DetailsPanel === "function"
            ? props.DetailsPanel(props.SelectedRowId, selectedRow)
            : null;

        const headerButtons = (props.DetailsPanelHeaderButtons || []).map((button, index) => (
            <Button
                key={index}
                Class="eone-ui-button-container-tertiary"
                Label={button.Label}
                Title={button.Title || button.Label}
                Icon={button.Icon}
                OnClick={button.OnClick}
                Disabled={button.Disabled}
                DataTestId={getDataTestId(props.DataTestId, "details-panel-button", button.Label)} />
        ));

        const setDetailsPanelOpen = props.TableState?.SetDetailsPanelOpen;
        const closeButton = typeof setDetailsPanelOpen === "function" ? (
            <Button
                Class="eone-ui-button-container-tertiary"
                Icon={faTimes}
                Title="Hide details"
                OnClick={() => setDetailsPanelOpen(false)}
                DataTestId={getDataTestId(props.DataTestId, "details-panel-close")} />
        ) : null;

        return (
            <aside className="eone-ui-table-details-panel" data-testid={getDataTestId(props.DataTestId, "details-panel")}>
                <div className="eone-ui-table-details-panel-header">
                    <div className="eone-ui-table-details-panel-title">{props.DetailsPanelTitle || "Details"}</div>
                    <div className="eone-ui-table-details-panel-header-buttons">
                        {headerButtons}
                        {closeButton}
                    </div>
                </div>
                <div className={`eone-ui-table-details-panel-body${selectedRow == null ? " eone-ui-table-details-panel-body-empty" : ""}`}>
                    {content}
                </div>
            </aside>
        );
    };

    const getSidePanel = () => {
        if (!sidePanelOpen) return null;

        const content = typeof props.SidePanel === "function"
            ? props.SidePanel()
            : props.SidePanel;

        const headerButtons = (props.SidePanelHeaderButtons || []).map((button, index) => (
            <Button
                key={index}
                Class="eone-ui-button-container-tertiary"
                Label={button.Label}
                Title={button.Title || button.Label}
                Icon={button.Icon}
                OnClick={button.OnClick}
                Disabled={button.Disabled}
                DataTestId={getDataTestId(props.DataTestId, "side-panel-button", button.Label)} />
        ));

        return (
            <aside className="eone-ui-table-details-panel" data-testid={getDataTestId(props.DataTestId, "side-panel")}>
                <div className="eone-ui-table-details-panel-header">
                    <div className="eone-ui-table-details-panel-title">{props.SidePanelTitle}</div>
                    <div className="eone-ui-table-details-panel-header-buttons">
                        {headerButtons}
                    </div>
                </div>
                <div className="eone-ui-table-details-panel-body">
                    {content}
                </div>
            </aside>
        );
    };

    const table = (
        <Table
            Heading={props.Heading}
            RowIcons={props.RowIcons}
            HeadingClass={props.HeadingClass}
            HeaderButtons={!props.Loading && hasRows ? props.TableHeaderButtons : null}
            PersistSortAndFilter={props.PersistSortAndFilter}
            FilterSelectUI={props.FilterSelectUI}
            ShowFilterAlways={props.ShowFilterAlways}
            StickyHeader={props.StickyHeader ?? true}
            Search={props.Search && !props.Loading && props.Rows && props.Rows.length > 0}
            OnSearch={props.TableState?.SetSearch}
            SearchValue={props.TableState?.Search}
            RecentSearches={props.RecentSearches || props.TableState?.RecentSearches}
            OnSaveSearch={props.OnSaveSearch || props.TableState?.SaveSearch}
            OnDeleteSearch={props.OnDeleteSearch || props.TableState?.DeleteSearch}
            OnClearSearches={props.OnClearSearches || props.TableState?.ClearRecentSearches}
            ShowSelectTableColumns={props.ShowSelectColumns && !props.Loading && props.Rows && props.Rows.length > 0}
            ShowSelectTableRows={props.ShowSelectRows && !props.Loading && props.Rows && props.Rows.length > 0}
            SelectTableRows={props.SelectRows}
            ShowRefresh={props.ShowRefresh && !props.Loading && props.Rows && props.Rows.length > 0}
            Refresh={props.Refresh}
            Loading={props.Loading}
            Columns={props.Columns}
            HideTableHeader={props.HideTableHeader}
            SelectedColumns={props.SelectedColumns}
            HorizontalScroll={props.HorizontalScroll}
            Rows={props.Rows}
            Buttons={props.RowButtons}
            CollapseHiddenButtons={props.CollapseHiddenRowButtons}
            FixedRowButtons={props.FixedRowButtons}
            IdField={props.IdField}
            KeyByIdField={props.KeyByIdField}
            Locale={props.Locale}
            Reorderable={props.Reorderable}
            Reorder={reorderHandler}
            TableState={props.TableState}
            Style={props.TableStyle}
            EmptyTitle={props.EmptyTitle}
            EmptyIcon={props.EmptyIcon}
            EmptyMessage={props.EmptyMessage}
            PrimaryEmptyPrompts={props.PrimaryEmptyPrompts ?? props.EmptyPrompts} //TODO: using this for now need to change all tables
            SecondaryEmptyPrompts={props.SecondaryEmptyPrompts}
            ChangeValue={changeValue}
            UpdateValue={updateValue}
            ShowSelectColumns={props.ShowSelectColumns}
            BodyHeight={props.BodyHeight}
            SelectedRowId={props.SelectedRowId}
            SelectRow={props.SelectRow}
            OnRowDoubleClick={props.OnRowDoubleClick}
            DataTestId={props.DataTestId}
            Tabs={props.Tabs}
            TabAlignment={props.TabAlignment}
            ActiveTab={props.ActiveTab}
            ChangeTab={props.ChangeTab}
            TabDataTestId={props.TabDataTestId}
            HeaderForm={props.HeaderForm}
            HeaderFormBelowTabs={props.HeaderFormBelowTabs}
            FooterForm={props.FooterForm}
            OnColumnResize={props.OnColumnResize}
            AllowViewSwitching={props.AllowViewSwitching}
            CardIconType={props.CardIconType}
            CardIconInitialsColumn={props.CardIconInitialsColumn}
            CardIconAvatarColumn={props.CardIconAvatarColumn}
            CardIconImageColumn={props.CardIconImageColumn}
            CardIconSeedColumn={props.CardIconSeedColumn}
            CardVersionField={props.CardVersionField}
            CardVersionCountField={props.CardVersionCountField}
            CardColumns={props.CardColumns}
            CardIconImageHideBackground={props.CardIconImageHideBackground}
            CustomCard={props.CustomCard}
            AllowDetailsPanel={props.AllowDetailsPanel}
            DetailsPanelOpen={props.TableState?.DetailsPanelOpen}
            OnDetailsPanelToggle={props.TableState?.SetDetailsPanelOpen}
            MaxHeightWithBreadcrumb={props.MaxHeightWithBreadcrumb}
        />
    );

    return (
        <Panel
            EmptyTitle={props.EmptyTitle}
            MaxHeightWithBreadcrumb={props.MaxHeightWithBreadcrumb}
            Title={props.Title}
            BodyClass={getPanelBodyClass()}
            HeaderButtons={props.HeaderButtons}
            BodyHeight={props.BodyHeight}
            Visible={props.Visible}
            Collapsible={props.Collapsible}
            Collapsed={props.Collapsed}
            OnCollapse={collapseHandler}
            Style={props.Style}
            DataTestId={props.DataTestId} 
            Card={props.Card}>
                
            {props.PanelHeaderContent}

            {/* Both flavours, same as Panel: a host with a line of literal copy wants ExplanationString,
                which skips the lazily loaded Markdown renderer. See PanelExplanation in panel.jsx. */}
            <PanelExplanation Explanation={props.Explanation} ExplanationString={props.ExplanationString} />

            {detailsPanelOpen ? (
                <div className="eone-ui-panel-table-with-details">
                    {table}
                    <HorizontalDragPanel
                        MinWidth={props.MinDetailsPanelWidth ?? 280}
                        MaxWidth={props.MaxDetailsPanelWidth ?? 800}
                        InitialWidth={props.InitialDetailsPanelWidth ?? 360}>
                        {getDetailsPanel()}
                    </HorizontalDragPanel>
                </div>
            ) : sidePanelOpen ? (
                <div className="eone-ui-panel-table-with-details">
                    {table}
                    <HorizontalDragPanel
                        MinWidth={props.MinSidePanelWidth ?? 240}
                        MaxWidth={props.MaxSidePanelWidth ?? 600}
                        InitialWidth={props.InitialSidePanelWidth ?? 320}>
                        {getSidePanel()}
                    </HorizontalDragPanel>
                </div>
            ) : (
                table
            )}
        </Panel>
    );
}

export { TablePanel };
